import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Spinner,
  Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faRotate,
  faTrashCan,
  faTriangleExclamation,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import CopyButton from "../Common/CopyButton";

const ALL_TOPICS = [
  { value: "order.created", label: "Đơn mới (order.created)" },
  { value: "order.updated", label: "Đơn cập nhật (order.updated)" },
];

const WooWebhookManager = ({ storeId, store }) => {
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [deliveryUrl, setDeliveryUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedTopics, setSelectedTopics] = useState(
    ALL_TOPICS.map((t) => t.value)
  );
  const [deletingId, setDeletingId] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventCounts, setEventCounts] = useState({});
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventStatusFilter, setEventStatusFilter] = useState("");

  const hasCredentials = !!(store?.consumer?.key && store?.consumer?.secret);

  const fetchList = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError("");
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.store.WEBHOOKS,
        pathParams: { id: storeId },
        type: "GET",
      });
      if (data?.message && !Array.isArray(data?.webhooks)) {
        setError(data.message);
      } else {
        setWebhooks(Array.isArray(data?.webhooks) ? data.webhooks : []);
        setDeliveryUrl(data?.deliveryUrl || "");
      }
    } catch (err) {
      setError(err.message || "Không tải được danh sách webhook.");
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const fetchEvents = useCallback(async () => {
    if (!storeId) return;
    setEventsLoading(true);
    try {
      const query = { limit: 50 };
      if (eventStatusFilter) query.status = eventStatusFilter;
      const data = await Utils._apiRequest({
        path: Enums.endpoints.store.WEBHOOK_EVENTS,
        pathParams: { id: storeId },
        query,
        type: "GET",
      });
      if (Array.isArray(data?.events)) {
        setEvents(data.events);
        setEventCounts(data.counts || {});
      }
    } catch (err) {
      // silent — log block is auxiliary
    } finally {
      setEventsLoading(false);
    }
  }, [storeId, eventStatusFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const onToggleTopic = (value) => {
    setSelectedTopics((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const onRegister = async () => {
    if (!hasCredentials) {
      setError(
        "Store chưa có consumer key/secret. Hãy kết nối WooCommerce trước."
      );
      return;
    }
    if (!selectedTopics.length) {
      setError("Vui lòng chọn ít nhất 1 loại sự kiện.");
      return;
    }
    setRegistering(true);
    setError("");
    setSuccess("");
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.store.WEBHOOKS,
        pathParams: { id: storeId },
        type: "POST",
        payload: { topics: selectedTopics },
      });
      if (data?.message && !Array.isArray(data?.webhooks)) {
        setError(data.message);
      } else {
        setWebhooks(data.webhooks || []);
        setDeliveryUrl(data.deliveryUrl || "");
        const createdCount = (data.created || []).length;
        setSuccess(
          createdCount > 0
            ? `Đã đăng ký ${createdCount} webhook mới.`
            : "Tất cả webhook đã tồn tại từ trước."
        );
      }
    } catch (err) {
      setError(err.message || "Đăng ký webhook thất bại.");
    } finally {
      setRegistering(false);
    }
  };

  const onDelete = async (webhookId) => {
    if (!window.confirm(`Xóa webhook #${webhookId} khỏi WooCommerce site?`))
      return;
    setDeletingId(webhookId);
    setError("");
    setSuccess("");
    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.store.WEBHOOK_DETAIL,
        pathParams: { id: storeId, webhookId },
        type: "DELETE",
      });
      if (data?.message && !Array.isArray(data?.webhooks)) {
        setError(data.message);
      } else {
        setWebhooks(data.webhooks || []);
        setSuccess(`Đã xóa webhook #${webhookId}.`);
      }
    } catch (err) {
      setError(err.message || "Xóa webhook thất bại.");
    } finally {
      setDeletingId(null);
    }
  };

  const renderStatus = (status) => {
    const variant =
      status === "active"
        ? "success"
        : status === "paused"
          ? "warning"
          : "secondary";
    return <Badge bg={variant}>{status || "unknown"}</Badge>;
  };

  return (
    <div className="my-3 p-3 bg-body rounded shadow-sm feature-box woo-webhook-manager">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
        <h6 className="heading mb-0">Webhook nhận order từ WooCommerce</h6>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={fetchList}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faRotate} className="me-1" spin={loading} />
          Tải lại
        </Button>
      </div>

      {!hasCredentials && (
        <Alert variant="warning" className="d-flex align-items-start mb-3">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="me-2 mt-1"
          />
          <div>
            Store này chưa có <code>consumer key/secret</code>. Hãy kết nối
            WooCommerce qua plugin trước khi đăng ký webhook.
          </div>
        </Alert>
      )}

      {deliveryUrl && (
        <div className="mb-3 small">
          <div className="text-muted mb-1">Delivery URL (server đang nghe):</div>
          <div className="d-flex align-items-center">
            <code className="flex-grow-1 text-break me-2">{deliveryUrl}</code>
            <CopyButton textToCopy={deliveryUrl} />
          </div>
        </div>
      )}

      <div className="mb-3">
        <div className="small text-muted mb-2">Loại sự kiện cần đăng ký:</div>
        <div className="d-flex flex-wrap gap-3">
          {ALL_TOPICS.map((t) => (
            <Form.Check
              key={t.value}
              type="checkbox"
              id={`woo-topic-${t.value}`}
              label={t.label}
              checked={selectedTopics.includes(t.value)}
              onChange={() => onToggleTopic(t.value)}
              disabled={registering}
            />
          ))}
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="py-2 mb-3">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="py-2 mb-3 d-flex align-items-center">
          <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
          {success}
        </Alert>
      )}

      <div className="mb-3">
        <Button
          variant="primary"
          onClick={onRegister}
          disabled={registering || !hasCredentials}
        >
          {registering ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Đang đăng ký...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Đăng ký webhook
            </>
          )}
        </Button>
      </div>

      <Table size="sm" responsive borderless className="align-middle mb-0">
        <thead className="text-muted small">
          <tr>
            <th>ID</th>
            <th>Sự kiện</th>
            <th>Trạng thái</th>
            <th>Đăng ký lúc</th>
            <th className="text-end">Hành động</th>
          </tr>
        </thead>
        <tbody>
          {loading && webhooks.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center text-muted py-3">
                <Spinner animation="border" size="sm" className="me-2" />
                Đang tải...
              </td>
            </tr>
          ) : webhooks.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center text-muted py-3">
                <i>Chưa có webhook nào được đăng ký.</i>
              </td>
            </tr>
          ) : (
            webhooks.map((w) => (
              <tr key={w.id}>
                <td>#{w.id}</td>
                <td>
                  <code>{w.topic}</code>
                </td>
                <td>{renderStatus(w.status)}</td>
                <td className="small text-muted">
                  {w.createdAt
                    ? Utils.dateFormat?.(w.createdAt, true) ||
                      new Date(w.createdAt).toLocaleString()
                    : "-"}
                </td>
                <td className="text-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onDelete(w.id)}
                    disabled={deletingId === w.id}
                  >
                    {deletingId === w.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faTrashCan} />
                    )}
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div className="border-top mt-4 pt-3">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <h6 className="mb-0">Lịch sử nhận webhook</h6>
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              style={{ width: 180 }}
              value={eventStatusFilter}
              onChange={(e) => setEventStatusFilter(e.target.value)}
            >
              <option value="">Tất cả trạng thái</option>
              <option value="processed">Đã xử lý</option>
              <option value="deleted">Đã xóa</option>
              <option value="ignored">Bỏ qua</option>
              <option value="rejected">Bị từ chối</option>
              <option value="error">Lỗi</option>
            </Form.Select>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={fetchEvents}
              disabled={eventsLoading}
            >
              <FontAwesomeIcon
                icon={faRotate}
                className="me-1"
                spin={eventsLoading}
              />
              Tải lại
            </Button>
          </div>
        </div>

        {Object.keys(eventCounts).length > 0 && (
          <div className="mb-2 small">
            {Object.entries(eventCounts).map(([k, v]) => (
              <Badge bg="light" text="dark" className="me-2" key={k}>
                {k}: <strong>{v}</strong>
              </Badge>
            ))}
          </div>
        )}

        <Table size="sm" responsive borderless className="align-middle mb-0 small">
          <thead className="text-muted">
            <tr>
              <th>Thời gian</th>
              <th>Topic</th>
              <th>Order</th>
              <th>Trạng thái</th>
              <th>HTTP</th>
              <th>Thông tin</th>
            </tr>
          </thead>
          <tbody>
            {eventsLoading && events.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Đang tải...
                </td>
              </tr>
            ) : events.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  <i>Chưa có sự kiện webhook nào.</i>
                </td>
              </tr>
            ) : (
              events.map((ev) => {
                const statusVariant =
                  ev.status === "processed" || ev.status === "deleted"
                    ? "success"
                    : ev.status === "ignored"
                      ? "secondary"
                      : ev.status === "rejected"
                        ? "warning"
                        : "danger";
                return (
                  <tr key={ev._id}>
                    <td className="text-muted">
                      {new Date(ev.createdAt).toLocaleString()}
                    </td>
                    <td><code>{ev.topic || "-"}</code></td>
                    <td>{ev.orderId ? `#${ev.orderId}` : "-"}</td>
                    <td>
                      <Badge bg={statusVariant}>{ev.status}</Badge>
                    </td>
                    <td>{ev.httpStatus || "-"}</td>
                    <td className="text-truncate" style={{ maxWidth: 360 }}>
                      {ev.message || ""}
                      {ev.durationMs != null && (
                        <span className="text-muted ms-2">
                          ({ev.durationMs}ms)
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default WooWebhookManager;
